import { useState } from "react";
import {
  WEEKEND_DAYS, COURTS, HALF_SLOTS, TIER_COLORS, courtTypeOf,
  PRIVATE_DURATION_OPTIONS, OPEN_PLAY_DURATION_OPTIONS, WEEKEND_SPLIT_TIME,
} from "../utils/tiers";
import { formatRange } from "../utils/time";

// Create/edit a weekend session — either an "open play" court booking (one or
// more members, no coach) or a "private" lesson (one coach + one member).
// Reuses the group data shape so the week grid, coach hours, and conflict
// checks all pick these up automatically.
export default function WeekendSessionModal({
  mode, session, prefill = {}, players, coaches = [],
  onCreate, onUpdate, onDelete, onClose,
}) {
  const editing = mode === "edit";
  const [kind, setKind] = useState(session?.kind || "openplay");
  const [day, setDay] = useState(session?.day || prefill.day || WEEKEND_DAYS[0]);
  const [startTime, setStartTime] = useState(session?.startTime || prefill.startTime || WEEKEND_SPLIT_TIME);
  const [court, setCourt] = useState(session?.court || prefill.court || COURTS[0]);
  const [duration, setDuration] = useState(session?.duration || (kind === "private" ? 1 : 1));
  const [coachId, setCoachId] = useState(session?.coachId || "");
  const [playerIds, setPlayerIds] = useState(session?.playerIds || []);
  const [search, setSearch] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);

  const durationOptions = kind === "private" ? PRIVATE_DURATION_OPTIONS : OPEN_PLAY_DURATION_OPTIONS;
  const playerMap = Object.fromEntries(players.map(p => [p.id, p]));
  const tc = TIER_COLORS[kind === "private" ? "Private" : "Open Play"];

  function switchKind(k) {
    setKind(k);
    // Keep durations valid for the new type; privates keep one player max.
    const opts = k === "private" ? PRIVATE_DURATION_OPTIONS : OPEN_PLAY_DURATION_OPTIONS;
    if (!opts.includes(duration)) setDuration(opts.includes(1) ? 1 : opts[0]);
    if (k === "private" && playerIds.length > 1) setPlayerIds(playerIds.slice(0, 1));
  }

  function togglePlayer(id) {
    if (kind === "private") {
      setPlayerIds(playerIds[0] === id ? [] : [id]);
    } else {
      setPlayerIds(playerIds.includes(id) ? playerIds.filter(x => x !== id) : [...playerIds, id]);
    }
    setSearch("");
  }

  const candidates = search.trim()
    ? players.filter(p => !playerIds.includes(p.id) && p.name.toLowerCase().includes(search.toLowerCase())).slice(0, 6)
    : [];

  const valid = day && startTime && court && duration &&
    (kind === "private" ? Boolean(coachId) : true);

  function save() {
    if (!valid) return;
    const data = {
      kind,
      tier: kind === "private" ? "Private" : "Open Play",
      day, startTime, court,
      courtType: courtTypeOf(court),
      duration: Number(duration),
      coachId: kind === "private" ? coachId : null,
      playerIds,
    };
    if (editing) onUpdate(session.id, data);
    else onCreate(data);
    onClose();
  }

  const labelCls = "block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1";
  const fieldCls = "border border-gray-300 rounded px-3 py-1.5 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-400";

  return (
    <div className="fixed inset-0 bg-black/40 flex items-start sm:items-center justify-center z-50 p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg my-8" onClick={e => e.stopPropagation()}>
        <div className={`flex items-center justify-between px-5 py-3 border-b ${tc.border} ${tc.bg} rounded-t-xl`}>
          <h2 className={`text-base font-bold ${tc.text}`}>
            {editing ? "Edit weekend session" : "New weekend session"}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl leading-none">×</button>
        </div>

        <div className="p-5 space-y-4">
          {/* Type toggle */}
          <div className="flex gap-1 bg-gray-100 border border-gray-200 rounded-lg p-1">
            {[["openplay", "Open Play"], ["private", "Private"]].map(([k, label]) => (
              <button
                key={k}
                onClick={() => switchKind(k)}
                className={`flex-1 text-sm font-semibold rounded-md py-1.5 transition-colors ${
                  kind === k ? "bg-brand-blue text-white shadow-sm" : "text-gray-500 hover:text-gray-800"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Day / court */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Day</label>
              <select value={day} onChange={e => setDay(e.target.value)} className={fieldCls}>
                {WEEKEND_DAYS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Court</label>
              <select value={court} onChange={e => setCourt(e.target.value)} className={fieldCls}>
                {COURTS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          {/* Start / duration */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Start time</label>
              <select value={startTime} onChange={e => setStartTime(e.target.value)} className={fieldCls}>
                {HALF_SLOTS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Length</label>
              <select value={duration} onChange={e => setDuration(Number(e.target.value))} className={fieldCls}>
                {durationOptions.map(h => (
                  <option key={h} value={h}>{h === 0.5 ? "30 min" : h === 1 ? "1 hour" : `${h} hours`}</option>
                ))}
              </select>
            </div>
          </div>
          <p className="text-xs text-gray-400 -mt-1">{day} · {formatRange(startTime, Number(duration))} · {court}</p>

          {/* Coach (private only) */}
          {kind === "private" && (
            <div>
              <label className={labelCls}>Coach</label>
              <select value={coachId} onChange={e => setCoachId(e.target.value)} className={fieldCls}>
                <option value="">Select a coach…</option>
                {coaches.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              {!coachId && <p className="text-xs text-amber-600 mt-1">A private lesson needs a coach.</p>}
            </div>
          )}

          {/* Members */}
          <div>
            <label className={labelCls}>{kind === "private" ? "Member" : "Members"}</label>
            {playerIds.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {playerIds.map(id => (
                  <span key={id} className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-700 rounded-full pl-2.5 pr-1 py-0.5">
                    {playerMap[id]?.name || id}
                    <button onClick={() => togglePlayer(id)} className="text-gray-400 hover:text-red-600 text-sm leading-none">×</button>
                  </span>
                ))}
              </div>
            )}
            {(kind === "openplay" || playerIds.length === 0) && (
              <>
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder={kind === "private" ? "Search for the member…" : "Search to add members…"}
                  className={fieldCls}
                />
                {candidates.length > 0 && (
                  <div className="mt-1 border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-44 overflow-y-auto">
                    {candidates.map(p => (
                      <button key={p.id} onClick={() => togglePlayer(p.id)} className="w-full text-left px-3 py-1.5 text-sm hover:bg-blue-50">
                        {p.name} <span className="text-xs text-gray-400">{p.tier}</span>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
            {kind === "openplay" && playerIds.length === 0 && (
              <p className="text-xs text-gray-400 mt-1">Optional — reserve the court without naming members if you like.</p>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-gray-200">
          {editing ? (
            confirmDelete ? (
              <button onClick={() => { onDelete(session.id); onClose(); }} className="text-sm bg-red-600 hover:bg-red-700 text-white rounded px-3 py-1.5 font-semibold">Confirm delete</button>
            ) : (
              <button onClick={() => setConfirmDelete(true)} onBlur={() => setConfirmDelete(false)} className="text-sm text-gray-400 hover:text-red-600 border border-gray-300 rounded px-3 py-1.5">Delete</button>
            )
          ) : <span />}
          <div className="flex gap-2">
            <button onClick={onClose} className="text-sm border border-gray-300 hover:bg-gray-50 text-gray-600 rounded px-4 py-1.5">Cancel</button>
            <button onClick={save} disabled={!valid} className="text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white font-medium rounded px-4 py-1.5 transition-colors">
              {editing ? "Save" : "Create session"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
